import _, {ceil, chain, range, times} from 'lodash';
import memoize from 'lru-memoize';
import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {
  build12Or24hFullOptionsForFormat,
  TimeFormatsEnum,
} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {FocusModesEnum} from '../../../../libs/shared/core/src/helpers/types/scrollerTypes';
import {DateTimeFormatterBase} from '../../../../libs/shared/ui/src/components/datetime/dateTimeFormatterBase';
import {Dropdown, DropdownProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {VariableHeightScroller} from '../scroll/variableHeightScroller';

// Only allow 10-15-30 minute increments
export type IncrementMinutesValues = 10 | 15 | 30;

/*
 * Constants.
 */

const getTimeValues = memoize()((
  minTime: DateTime | undefined,
  maxTime: DateTime | undefined,
  incrementMinutes: IncrementMinutesValues,
  displayOvernight: boolean,
) => {
  // +1 since minTime excludes the value.
  const minBoundary = minTime ? getMinutes(minTime) + 1 : 0;
  const maxBoundary = getMaxBoundary(minBoundary, getMinutes(maxTime), displayOvernight);

  return chain(range(48))
    .map((hour) => {
      const hourMinute = hour * MINUTES_IN_HOUR;
      const hourDates = times(
        ceil(MINUTES_IN_HOUR / incrementMinutes),
        (i) => hourMinute + incrementMinutes * i,
      );

      return hourDates.filter((d) => minBoundary <= d && d < maxBoundary);
    })
    .flatten()
    .value();
});

/*
 * Props.
 */

export interface TimeDropdownProps extends DropdownProps {
  timeFormat: TimeFormatsEnum;
  selectedTime?: DateTime;
  onTimeSelect?: (arg: DateTime | undefined) => void;

  /** Excluded if defined. */
  minTime?: DateTime;
  /** Excluded if defined. */
  maxTime?: DateTime;
  /** Explicitly includes maxTime. */
  includeMaxTime?: boolean;
  incrementMinutes?: IncrementMinutesValues;
  emptyValue?: string;
  width?: number;

  /** Allow to pick up to 24h after the minTime. Display a Next day label. */
  displayOvernight?: boolean;
}

/*
 * Style.
 */

const StyledDropdownScroller = styled(VariableHeightScroller)`
  margin: -7px 0;
  padding: 7px 0;
`;

const StyledNextDayDropdownItem = styled(DropdownItem)`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  margin-top: 8px;
  padding-top: 8px;
`;

const StyledNextDayDropdownItemContent = styled(DropdownItemContent)`
  color: ${(p) => p.theme.greys.shade60};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};
`;

/*
 * Component.
 */

export const TimeDropdown: FC<TimeDropdownProps> = (props) => {
  const {selectedTime} = props;
  const focusKey = getMinutes(selectedTime);
  const width = props.width || (props.timeFormat === TimeFormatsEnum.H12 ? 130 : 110);
  return (
    <Dropdown {...props} width={width} maxHeight={200}>
      <DropdownList {...props}>
        <StyledDropdownScroller focusKey={focusKey} focusMode={FocusModesEnum.MIDDLE}>
          {renderItems(props)}
        </StyledDropdownScroller>
      </DropdownList>
    </Dropdown>
  );
};

function renderItems(props: TimeDropdownProps) {
  const {
    minTime,
    maxTime,
    includeMaxTime,
    selectedTime,
    timeFormat,
    onTimeSelect,
    emptyValue,
    displayOvernight = false,
  } = props;
  // Default time increments to 30 minutes if no incrementMinutes prop is passed
  const incrementMinutes = props.incrementMinutes || 30;
  const format = build12Or24hFullOptionsForFormat(timeFormat);
  let selectedTimeInMinutes = getMinutes(props.selectedTime);
  const minTimeInMinutes = getMinutes(minTime);

  if (
    displayOvernight &&
    selectedTimeInMinutes !== undefined &&
    minTimeInMinutes !== undefined &&
    selectedTimeInMinutes <= minTimeInMinutes
  ) {
    // Add 24H if selected time is within the next day.
    selectedTimeInMinutes += MINUTES_IN_DAY;
  }

  const maxTimeInMinutes = getMinutes(maxTime);

  const minuteValues = _(getTimeValues(minTime, maxTime, incrementMinutes, displayOvernight))
    .concat(selectedTimeInMinutes !== undefined ? [selectedTimeInMinutes] : [])
    .concat(includeMaxTime && maxTimeInMinutes !== undefined ? [maxTimeInMinutes] : [])
    .uniq()
    .sortBy((minutes) => minutes);

  const firstOvernightValue = minuteValues.find((minute) => minute >= MINUTES_IN_DAY);

  return minuteValues
    .map((minutes) => {
      const isChecked = minutes === selectedTimeInMinutes;
      const showNextDay = minutes === firstOvernightValue;

      const dateTime = DateTime.local()
        .startOf('minute')
        .set({
          hour: Math.trunc(minutes / MINUTES_IN_HOUR),
          minute: minutes % MINUTES_IN_HOUR,
        });

      return (
        <React.Fragment key={minutes % MINUTES_IN_DAY}>
          {showNextDay ? (
            <StyledNextDayDropdownItem isNotInteractive>
              <StyledNextDayDropdownItemContent>
                <Trans id="time-dropdown-next-day-label">Next day</Trans>
              </StyledNextDayDropdownItemContent>
            </StyledNextDayDropdownItem>
          ) : undefined}
          <DropdownItem isChecked={isChecked} onSelect={() => onTimeSelect && onTimeSelect(dateTime)}>
            <DropdownItemContent isChecked={isChecked}>
              <DateTimeFormatterBase value={dateTime} format={format} />
            </DropdownItemContent>
          </DropdownItem>
        </React.Fragment>
      );
    })
    .unshift(
      // @ts-expect-error the types of `unshift` don't allow an `undefined` value, which makes sense but we run `compact()` just after so it should be safe
      emptyValue ? (
        <DropdownItem key="empty-value" isChecked={!selectedTime} onSelect={() => onTimeSelect?.(undefined)}>
          <DropdownItemContent isChecked={!selectedTime}>{props.emptyValue}</DropdownItemContent>
        </DropdownItem>
      ) : undefined,
    )
    .compact()
    .value();
}

function getMinutes(time: DateTime): number;
function getMinutes(time: DateTime | undefined): number | undefined;
function getMinutes(time: DateTime | undefined) {
  if (!time) {
    return undefined;
  }

  return time.hour * 60 + time.minute;
}

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;

function getMaxBoundary(minTime: number, maxTime: number | undefined, displayOvernight: boolean): number {
  if (!displayOvernight || minTime === undefined) {
    return maxTime ?? MINUTES_IN_DAY;
  }

  if (maxTime === undefined) {
    return minTime + MINUTES_IN_DAY;
  }

  if (maxTime < minTime) {
    return maxTime + MINUTES_IN_DAY;
  }

  return maxTime;
}
